import { DetectedCatalogItem, DetectedMentions, DetectedObjective } from '@omni/ai/features/detected-mentions';
import {
  buildDictationCatalog,
  CatalogBrand,
  CatalogKeyMessage,
  CatalogProduct,
} from '@omni/ai/features/dictation-catalog';
import {
  accountNameVariants,
  contactNameVariants,
  keyMessageNameVariants,
  productNameVariants,
  stripHonorific,
} from './entity-projections';
import { ResolvedMention, resolveMention, resolveMentions } from './mention-resolver';

/** A stored record projected to the two fields resolution needs: its id and display name. */
export interface DictationRecord {
  id: string;
  name: string;
}

/**
 * The rep's offline collections. Contacts and accounts are plain {@link DictationRecord}s;
 * products and key messages carry their catalog shape so the applier keeps each key
 * message's owning product.
 */
export interface DictationCollections {
  contacts: DictationRecord[];
  accounts: DictationRecord[];
  products: CatalogProduct[];
  keyMessages: CatalogKeyMessage[];
}

/**
 * A detected objective with the customer it belongs to resolved. The review shows
 * the aim against that customer; only a row whose customer resolved can be written,
 * since a Next Call Objective is reached through its customer.
 */
export interface ResolvedObjective {
  objective: string;
  customer: ResolvedMention<DictationRecord>;
}

/**
 * The meeting a confirmed dictation will be applied to, projected to the fields the
 * review shows. A projection rather than the activity itself: the review renders it
 * and formats nothing, so the rep's own date and time preferences are applied once,
 * where the service that knows them lives.
 */
export interface TargetMeeting {
  id: string;
  subject: string;
  /** Date and time, already formatted for display. */
  when: string;
  location: string | null;
}

/**
 * A customer a note already names, carried as the record it points at. Unlike a
 * detected name this was never spoken, so it is placed in the review as a settled
 * match rather than being put through the matcher.
 */
export interface KnownCustomer {
  kind: 'contact' | 'account';
  record: DictationRecord;
}

/** Everything the structured review renders: the recap plus each detected name and how it resolved. */
export interface ResolvedDictation {
  subject: string;
  summary: string;
  contacts: ResolvedMention<DictationRecord>[];
  accounts: ResolvedMention<DictationRecord>[];
  products: ResolvedMention<CatalogProduct>[];
  keyMessages: ResolvedMention<CatalogKeyMessage>[];
  objectives: ResolvedObjective[];
}

/** An objective the rep kept, bound to the customer record it will be written against. */
export interface ConfirmedObjective {
  objective: string;
  customer: DictationRecord;
}

/**
 * What the rep confirmed in the review — the exact records and text to write onto
 * the meeting. Only included, resolved items survive here; the applier adds them
 * additively and never overwrites the rep's own fields.
 */
export interface ConfirmedDictation {
  subject: string;
  summary: string;
  contacts: DictationRecord[];
  accounts: DictationRecord[];
  products: CatalogProduct[];
  keyMessages: CatalogKeyMessage[];
  objectives: ConfirmedObjective[];
  /** Where the note was captured, when the rep kept it in the review. */
  location?: CapturedLocation | null;
  /** The rep ticked Transcript in the review, so the transcript is kept on the meeting as its own note. */
  includeTranscript?: boolean;
}

/** Where a note was captured, as the capturing device recorded it. */
export interface CapturedLocation {
  location: string | null;
  latitude: number | null;
  longitude: number | null;
}

/** The two fields the contact collection is projected from. */
export interface ContactSource {
  ID: string;
  fullName: string;
}

/** The two fields the account collection is projected from. */
export interface AccountSource {
  id: string;
  accountName: string;
}

/**
 * Project the rep's offline domain records into the {@link DictationCollections}
 * resolution matches against. Contacts and accounts map their id/name fields;
 * products and key messages come from the shared catalog builder. Pure, so both
 * meeting entry points build collections the same way.
 */
export function buildDictationCollections(
  contacts: ContactSource[],
  accounts: AccountSource[],
  brands: CatalogBrand[],
): DictationCollections {
  const catalog = buildDictationCatalog(brands);
  return {
    contacts: (contacts ?? []).map((c) => ({ id: c.ID, name: c.fullName })),
    accounts: (accounts ?? []).map((a) => ({ id: a.id, name: a.accountName })),
    products: catalog.products,
    keyMessages: catalog.keyMessages,
  };
}

/**
 * Whether the device holds the records a dictation's customers are matched against.
 *
 * An empty collection is not evidence that a spoken name is unknown. The records
 * are filled by the sync, and a dictation can run before that has finished, which
 * leaves every name unmatched for a reason the review cannot express. Callers
 * establish this before resolving so the two cases are not presented alike.
 */
export function canMatchCustomers(collections: DictationCollections | null | undefined): boolean {
  return (collections?.contacts?.length ?? 0) > 0;
}

const byName = <T extends DictationRecord>(project: (name: string) => string[]) => (record: T) => project(record.name);

/**
 * Collapse rows that landed on the same record. Mentions are de-duplicated by the
 * words the rep said, and one person is said several ways — "Dr. Majhi" and
 * "Abhijan" reach the same contact and would otherwise be two rows carrying the
 * same name. Rows that resolved to nothing are each kept: they have no record to
 * be the same as, and the rep needs to see every name that found nobody.
 */
function collapseByRecord<T extends DictationRecord>(rows: ResolvedMention<T>[]): ResolvedMention<T>[] {
  const seen = new Set<string>();
  return rows.filter((row) => {
    const id = row.top?.id;
    if (!id) {
      return true;
    }
    if (seen.has(id)) {
      return false;
    }
    seen.add(id);
    return true;
  });
}

/**
 * Resolve catalog items (products, key messages) id-first: when the detector
 * returned a catalog id that is really in the collection, trust it directly —
 * this is what lets a paraphrased key message land on the right record. Otherwise
 * fall back to a fuzzy name match. The displayed mention stays the spoken name.
 */
function resolveCatalogItems<T extends DictationRecord>(
  items: DetectedCatalogItem[],
  records: T[],
  project: (name: string) => string[],
  choose: (matches: T[]) => T = (matches) => matches[0],
): ResolvedMention<T>[] {
  // One id can name several entries: a key message is listed against each product
  // carrying it. Keeping them all lets the caller pick the one that fits what was
  // discussed, where a single-entry lookup would keep whichever came last.
  const byId = new Map<string, T[]>();
  for (const record of records) {
    const existing = byId.get(record.id);
    if (existing) {
      existing.push(record);
    } else {
      byId.set(record.id, [record]);
    }
  }
  const seen = new Set<string>();
  const resolved: ResolvedMention<T>[] = [];
  for (const item of items ?? []) {
    const mention = (item?.name ?? '').trim();
    if (!mention) continue;
    const key = mention.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);

    const matches = item.id ? byId.get(item.id) : undefined;
    const direct = matches?.length ? choose(matches) : undefined;
    if (direct) {
      resolved.push({ mention, status: 'matched', top: direct, candidates: [{ record: direct, score: 0 }] });
      continue;
    }
    const { status, top, candidates } = resolveMention(mention, records, byName(project));
    resolved.push({ mention, status, top, candidates });
  }
  return resolved;
}

/**
 * Resolve each objective's customer.
 *
 * An objective names someone the note named elsewhere, so a customer the list
 * above has already settled on is taken as settled here too. Judged on its own,
 * a shorter form of the same name comes back undecided, and the rep is asked to
 * confirm someone the screen has confirmed a few rows earlier.
 */
function resolveObjectives(
  objectives: DetectedObjective[],
  contacts: DictationRecord[],
  settled: ResolvedMention<DictationRecord>[],
): ResolvedObjective[] {
  const settledIds = new Set(settled.map((row) => row.top?.id).filter(Boolean) as string[]);
  const resolved: ResolvedObjective[] = [];
  for (const item of objectives ?? []) {
    const objective = (item?.objective ?? '').trim();
    const spoken = (item?.customer ?? '').trim();
    if (!objective || !spoken) continue;
    const match = resolveMention(spoken, contacts, byName(contactNameVariants), { prepareNeedle: stripHonorific });
    const settledCandidate = match.top && settledIds.has(match.top.id)
      ? match.top
      : match.candidates.find((c) => settledIds.has(c.record.id))?.record;
    resolved.push({
      objective,
      customer: settledCandidate
        ? { mention: spoken, status: 'matched', top: settledCandidate, candidates: match.candidates }
        : { mention: spoken, status: match.status, top: match.top, candidates: match.candidates },
    });
  }
  return resolved;
}

/**
 * Turn the detected mentions into per-category resolution rows the review screen
 * renders. Pure: contacts and accounts fuzzy-match on spoken name; products and
 * key messages resolve id-first against the grounding catalog, and each objective
 * binds to the customer it is for. The recap passes through untouched. The rep
 * confirms every row — nothing here writes to the meeting.
 */
export function resolveDictation(
  detected: DetectedMentions,
  collections: DictationCollections,
  known?: KnownCustomer | null,
): ResolvedDictation {
  const named = collapseByRecord(
    resolveCatalogItems(detected.products, collections.products, productNameVariants),
  );
  const discussed = new Set(named.map((row) => row.top?.id).filter(Boolean) as string[]);
  const keyMessages = collapseByRecord(
    resolveCatalogItems(detected.keyMessages, collections.keyMessages, keyMessageNameVariants,
      // A key message carried by several products belongs to the one the rep
      // discussed. Where none of them was named, the product it brings in is
      // shown as its own row for the rep to accept or refuse.
      (matches) => matches.find((m) => discussed.has(m.productId)) ?? matches[0]),
  );
  // Every spoken form, before the duplicates are folded away: an objective names
  // the customer in whichever form the rep used, which may be the one collapsed.
  const spokenFor = resolveMentions(detected.contacts, collections.contacts, byName(contactNameVariants), {
    prepareNeedle: stripHonorific,
  });
  return withKnownCustomer({
    subject: detected.subject,
    summary: detected.summary,
    contacts: collapseByRecord(spokenFor),
    accounts: collapseByRecord(resolveMentions(detected.accounts, collections.accounts, byName(accountNameVariants))),
    products: withImpliedProducts(named, keyMessages, collections.products),
    keyMessages,
    objectives: resolveObjectives(detected.objectives, collections.contacts, collapseByRecord(spokenFor)),
  }, known, collections);
}

/**
 * Give every key message's product a row of its own.
 *
 * A matched key message puts its product on the meeting whether or not the rep
 * named the product, so a product reaching the meeting without a row is one the
 * rep can neither see nor refuse. Added as a settled match, since the record is
 * the one the key message already points at.
 */
function withImpliedProducts(
  products: ResolvedMention<CatalogProduct>[],
  keyMessages: ResolvedMention<CatalogKeyMessage>[],
  known: CatalogProduct[],
): ResolvedMention<CatalogProduct>[] {
  const present = new Set(products.map((row) => row.top?.id).filter(Boolean) as string[]);
  const implied: ResolvedMention<CatalogProduct>[] = [];
  for (const row of keyMessages) {
    // Only a key message that settled brings its product in. One the matcher is
    // unsure of is shown for the rep to confirm, and a product presented as
    // settled on the strength of it would be surer than the thing it came from.
    const productId = row.status === 'matched' ? row.top?.productId : undefined;
    if (!productId || present.has(productId)) {
      continue;
    }
    const product = known.find((p) => p.id === productId);
    if (!product) {
      continue;
    }
    present.add(productId);
    implied.push({ mention: product.name, status: 'matched', top: product, candidates: [{ record: product, score: 0 }] });
  }
  return [...products, ...implied];
}

/**
 * Place a customer the note already names alongside the detected ones. It is put
 * in as a settled match rather than matched by name, so a name the rep never spoke
 * cannot land on a different record. A customer this device has not synced cannot
 * be attached at all, and shows as unresolved here rather than failing silently at
 * write time. A detected name that already found the same record wins, so the rep
 * never confirms one customer twice.
 */
function withKnownCustomer(
  resolved: ResolvedDictation,
  known: KnownCustomer | null | undefined,
  collections: DictationCollections,
): ResolvedDictation {
  if (!known?.record?.id) {
    return resolved;
  }
  const contact = known.kind === 'contact';
  const rows = contact ? resolved.contacts : resolved.accounts;
  // A row the matcher left undecided between several records, one of them this
  // one, is settled here rather than added beside: the note names this customer
  // outright, which is better evidence than a name the matcher could not place.
  const settled = rows.map((row) => {
    if (row.top?.id === known.record.id) {
      return row;
    }
    const candidate = row.candidates.find((c) => c.record.id === known.record.id);
    return candidate ? { ...row, status: 'matched' as const, top: candidate.record } : row;
  });
  if (settled.some((r) => r.top?.id === known.record.id)) {
    return contact ? { ...resolved, contacts: settled } : { ...resolved, accounts: settled };
  }
  const stored = (contact ? collections.contacts : collections.accounts).find((r) => r.id === known.record.id);
  const row: ResolvedMention<DictationRecord> = stored
    ? { mention: stored.name, status: 'matched', top: stored, candidates: [{ record: stored, score: 0 }] }
    : { mention: known.record.name, status: 'unmatched', top: null, candidates: [] };
  return contact
    ? { ...resolved, contacts: [row, ...resolved.contacts] }
    : { ...resolved, accounts: [row, ...resolved.accounts] };
}
